<template>
  <transition name="sk-pwa-slide">
    <div
      v-if="visible"
      class="sk-pwa-banner"
      role="dialog"
      aria-label="Install Screenkode app"
    >
      <button class="sk-pwa-close" aria-label="Dismiss" @click="dismiss">
        <i class="pi pi-times"></i>
      </button>

      <div class="sk-pwa-icon">
        <img :src="iconSrc" alt="Screenkode" />
      </div>

      <div class="sk-pwa-body">
        <p class="sk-pwa-title">Install Screenkode</p>

        <template v-if="mode === 'prompt'">
          <p class="sk-pwa-text">
            Add Screenkode to your device for a faster, full-screen, app-like
            experience.
          </p>
          <div class="sk-pwa-actions">
            <button class="sk-pwa-btn sk-pwa-btn--primary" @click="install">
              Install app
            </button>
            <button class="sk-pwa-btn sk-pwa-btn--ghost" @click="dismiss">
              Not now
            </button>
          </div>
        </template>

        <template v-else-if="mode === 'ios'">
          <p class="sk-pwa-text">
            Tap
            <span class="sk-pwa-inline">
              <i class="pi pi-upload"></i> Share
            </span>
            then
            <span class="sk-pwa-inline">Add to Home Screen</span>
            to install.
          </p>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import { DISPLAY_ROUTE, SCREEN_SCHEDULE_DISPLAY_ROUTE } from '@/router/routes';

const DISMISS_KEY = 'sk_pwa_install_dismissed_at';
// Once dismissed, stay quiet for this long before offering again.
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export default {
  name: 'PwaInstallPrompt',
  data() {
    return {
      // The captured beforeinstallprompt event (Chrome / Edge / Android).
      deferredPrompt: null,
      // 'prompt' -> native install available; 'ios' -> manual Safari hint.
      mode: null,
      installed: false,
    };
  },
  computed: {
    iconSrc() {
      const base = process.env.BASE_URL || '/';
      return `${base}icons/Icon.svg`;
    },
    onDisplayRoute() {
      const route = this.$route;
      if (!route) return false;
      // Match by route name — a screen's link or a screen schedule's link — with
      // a path fallback (`/display/...`) so the prompt is suppressed even if the
      // name isn't resolved as expected.
      if (route.name === DISPLAY_ROUTE || route.name === SCREEN_SCHEDULE_DISPLAY_ROUTE) return true;
      const path = String(route.path || '');
      return path === '/display' || path.startsWith('/display/');
    },
    visible() {
      // Never cover the kiosk/TV display — hidden on the display page and shown on
      // every other page. This is reactive to route changes, so a prompt armed
      // elsewhere stays hidden while on /display and reappears after leaving it.
      if (this.onDisplayRoute) return false;
      return !this.installed && this.mode != null;
    },
  },
  methods: {
    isStandalone() {
      if (typeof window === 'undefined') return false;
      const mm = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
      // iOS Safari exposes navigator.standalone instead of display-mode.
      return Boolean(mm || window.navigator.standalone === true);
    },
    isIos() {
      const ua = window.navigator.userAgent || '';
      const iOSDevice = /iphone|ipad|ipod/i.test(ua);
      // iPadOS 13+ reports as MacIntel but has touch points.
      const iPadOS = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
      return iOSDevice || iPadOS;
    },
    isIosSafari() {
      const ua = window.navigator.userAgent || '';
      // Only Safari on iOS can Add to Home Screen (Chrome/Firefox/Edge iOS cannot).
      return this.isIos() && /safari/i.test(ua) && !/crios|fxios|edgios|opios/i.test(ua);
    },
    recentlyDismissed() {
      try {
        const raw = window.localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const at = parseInt(raw, 10);
        if (!at) return false;
        return Date.now() - at < DISMISS_WINDOW_MS;
      } catch (e) {
        return false;
      }
    },
    onBeforeInstallPrompt(e) {
      // Stop Chrome's default mini-infobar; we show our own UI instead.
      e.preventDefault();
      this.deferredPrompt = e;
      if (this.isStandalone() || this.recentlyDismissed()) return;
      this.mode = 'prompt';
    },
    onAppInstalled() {
      this.installed = true;
      this.deferredPrompt = null;
      this.mode = null;
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch (e) {
        /* ignore */
      }
    },
    async install() {
      if (!this.deferredPrompt) return;
      const promptEvent = this.deferredPrompt;
      this.deferredPrompt = null;
      promptEvent.prompt();
      try {
        const { outcome } = await promptEvent.userChoice;
        if (outcome === 'accepted') {
          this.mode = null;
        } else {
          // Declined the native dialog — respect it and back off.
          this.rememberDismissal();
          this.mode = null;
        }
      } catch (e) {
        this.mode = null;
      }
    },
    dismiss() {
      this.rememberDismissal();
      this.mode = null;
    },
    rememberDismissal() {
      try {
        window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch (e) {
        /* ignore */
      }
    },
    maybeShowIosHint() {
      if (this.isStandalone() || this.recentlyDismissed()) return;
      if (this.isIosSafari()) {
        // Give the app a moment to settle before surfacing the hint — but never
        // arm it while on the display page.
        window.setTimeout(() => {
          if (!this.installed && !this.onDisplayRoute && this.mode == null) this.mode = 'ios';
        }, 2500);
      }
    },
  },
  mounted() {
    if (typeof window === 'undefined') return;
    if (this.isStandalone()) {
      this.installed = true;
      return;
    }
    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.onAppInstalled);
    this.maybeShowIosHint();
  },
  beforeUnmount() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.onAppInstalled);
  },
};
</script>

<style scoped>
.sk-pwa-banner {
  position: fixed;
  z-index: 100000;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 420px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid rgba(99, 102, 241, 0.18);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
  font-family: 'Poppins', system-ui, -apple-system, sans-serif;
}

.sk-pwa-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #94a3b8;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  line-height: 1;
}
.sk-pwa-close:hover {
  background: #f1f5f9;
  color: #475569;
}

.sk-pwa-icon {
  flex: 0 0 auto;
}
.sk-pwa-icon img {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: block;
  object-fit: contain;
}

.sk-pwa-body {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 18px;
}

.sk-pwa-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
}

.sk-pwa-text {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: #475569;
}

.sk-pwa-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 600;
  white-space: nowrap;
}
.sk-pwa-inline .pi {
  font-size: 0.72rem;
}

.sk-pwa-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.sk-pwa-btn {
  border: none;
  border-radius: 10px;
  padding: 9px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.sk-pwa-btn--primary {
  background: #6366f1;
  color: #ffffff;
}
.sk-pwa-btn--primary:hover {
  background: #4f46e5;
}
.sk-pwa-btn--ghost {
  background: transparent;
  color: #64748b;
}
.sk-pwa-btn--ghost:hover {
  background: #f1f5f9;
}

/* slide-up transition */
.sk-pwa-slide-enter-active,
.sk-pwa-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.sk-pwa-slide-enter-from,
.sk-pwa-slide-leave-to {
  transform: translate(-50%, 24px);
  opacity: 0;
}

@media (prefers-color-scheme: dark) {
  .sk-pwa-banner {
    background: #1e293b;
    border-color: rgba(129, 140, 248, 0.3);
  }
  .sk-pwa-title {
    color: #f8fafc;
  }
  .sk-pwa-text {
    color: #cbd5e1;
  }
  .sk-pwa-close:hover {
    background: #334155;
    color: #e2e8f0;
  }
  .sk-pwa-inline {
    background: #312e81;
    color: #c7d2fe;
  }
  .sk-pwa-btn--ghost {
    color: #cbd5e1;
  }
  .sk-pwa-btn--ghost:hover {
    background: #334155;
  }
}
</style>
